import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function NewsletterForm({ compact = false, defaultInterest = "both" }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    interest: defaultInterest,
    privacy_consent: false,
    marketing_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Consenso privacy obbligatorio.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { ...form, lang });
      toast.success(data.message || t("common.thanks"));
      setForm({ name: "", email: "", interest: defaultInterest, privacy_consent: false, marketing_consent: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form-compact">
        <div className="flex gap-2">
          <Input
            type="email"
            required
            placeholder={t("common.email")}
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="bg-transparent border-[#2C4544] text-white placeholder:text-[#6E7E7C] rounded-none"
            data-testid="newsletter-email-input"
            aria-label={t("common.email")}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit-button"
            className="shrink-0 rounded-full bg-white text-[#0F1F1F] px-5 text-xs uppercase tracking-editorial hover:bg-[#E5EBEA] disabled:opacity-50 transition-colors"
          >
            {loading ? "…" : t("common.subscribe")}
          </button>
        </div>
        <label className="flex items-start gap-2 text-xs text-[#8FA09E] cursor-pointer">
          <input
            type="checkbox"
            required
            checked={form.privacy_consent}
            onChange={(e) => setForm((f) => ({ ...f, privacy_consent: e.target.checked }))}
            className="mt-0.5"
            data-testid="newsletter-privacy-consent"
          />
          <span>{t("common.privacy_consent")}</span>
        </label>
      </form>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="newsletter-form"
    >
      <Input
        type="text"
        placeholder={t("common.name")}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        className="rounded-none border-brand/20 bg-white"
        aria-label={t("common.name")}
      />
      <Input
        type="email"
        required
        placeholder={t("common.email")}
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        className="rounded-none border-brand/20 bg-white"
        data-testid="newsletter-email-input-full"
        aria-label={t("common.email")}
      />
      <div className="md:col-span-2">
        <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">
          Interesse
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            { v: "pet", l: "Pet", color: "pet" },
            { v: "human", l: "Human", color: "human" },
            { v: "both", l: "Entrambe", color: "brand" },
          ].map((opt) => (
            <label
              key={opt.v}
              className={`inline-flex items-center gap-2 px-4 py-2 border cursor-pointer text-sm ${
                form.interest === opt.v ? "border-brand bg-brand text-white" : "border-soft text-brand"
              }`}
            >
              <input
                type="radio"
                name="nl-interest"
                value={opt.v}
                checked={form.interest === opt.v}
                onChange={() => setForm((f) => ({ ...f, interest: opt.v }))}
                className="sr-only"
              />
              {opt.l}
            </label>
          ))}
        </div>
      </div>
      <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
        <input
          type="checkbox"
          required
          checked={form.privacy_consent}
          onChange={(e) => setForm((f) => ({ ...f, privacy_consent: e.target.checked }))}
          data-testid="newsletter-privacy-consent-full"
          className="mt-1"
        />
        <span>{t("common.privacy_consent")}</span>
      </label>
      <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
        <input
          type="checkbox"
          checked={form.marketing_consent}
          onChange={(e) => setForm((f) => ({ ...f, marketing_consent: e.target.checked }))}
          className="mt-1"
        />
        <span>{t("common.marketing_consent")}</span>
      </label>
      <div className="md:col-span-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-full"
          className="rounded-full bg-brand text-white px-8 py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50 transition-colors"
        >
          {loading ? t("common.sending") : t("common.subscribe")}
        </button>
      </div>
    </form>
  );
}
